import { GoogleReCaptchaCheckbox, useGoogleReCaptcha } from "@google-recaptcha/react";
import React,{ ChangeEvent, FormEvent, useState } from "react";
import styled, { css } from "styled-components";
import { useUIContext } from "../../context/UIContext";
import { EvianSignUpData } from "../../models/Evian";
import { API } from "../../utils/API";
import { EVIAN_COLORS } from "../../utils/Colors";
import { formatUSPhoneNumber } from "../../utils/InputChecker";
import { EvianRegisterFootnote } from "./EvianRegisterFootnote";

const radii = {
  md: "10px",
  lg: "14px",
  xl: "18px",
};

const shadow = {
  inset: "inset 0 1px 0 rgba(255,255,255,0.4)",
};

const Form = styled.form`
  display: grid;
  gap: 16px;
`;

const Field = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  color: ${EVIAN_COLORS.text};
  font-weight: 600;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;

  &::after {
    content: "*";
    color: ${EVIAN_COLORS.primary};
    font-weight: 700;
  }
`;

type InputProps = { $invalid?: boolean };

const sharedInput = css<InputProps>`
  width: 100%;
  height: 44px;
  border: 1px solid ${({ $invalid }) => ($invalid ? "#ef4444" : EVIAN_COLORS.borderStrong)};
  border-radius: ${radii.md};
  padding: 10px 12px;
  background: #fff;
  color: ${EVIAN_COLORS.text};
  font-size: 15px;
  outline: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:focus {
    border-color: ${EVIAN_COLORS.primary};
    box-shadow: 0 0 0 4px ${EVIAN_COLORS.focusRing};
  }

  &::placeholder {
    color: ${EVIAN_COLORS.muted};
  }
`;

const Input = styled.input<InputProps>`
  ${sharedInput}
`;

const CheckboxRow = styled.div<InputProps>`
  display: grid;
  grid-template-columns: 20px 1fr;
  align-items: start;
  gap: 10px;
  padding-top: 4px;

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    accent-color: ${EVIAN_COLORS.primary};
    border: 1px solid ${({ $invalid }) => ($invalid ? "#ef4444" : EVIAN_COLORS.borderStrong)};
    border-radius: 6px;
    outline: none;

    &:focus {
      box-shadow: 0 0 0 4px ${EVIAN_COLORS.focusRing};
    }
  }

  label {
    font-size: 14px;
    color: ${EVIAN_COLORS.text};
    font-weight: 500;
  }

  a {
    color: ${EVIAN_COLORS.primary};
    text-decoration: underline;
  }
`;

const Submit = styled.button<{ disabled?: boolean }>`
  height: 48px;
  border: 1px solid ${EVIAN_COLORS.text};
  background: linear-gradient(0deg, #fafafa, #ffffff);
  box-shadow: ${shadow.inset};
  color: ${EVIAN_COLORS.text};
  font-weight: 700;
  border-radius: ${radii.md};
  cursor: pointer;
  transition: transform 0.05s ease, filter 0.15s ease, border-color 0.15s ease;

  &:hover {
    filter: brightness(0.98);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface Props {
  zone: string | null;
  setIsSuccessScreenVisible: (isSuccessScreenVisible: boolean) => void;
  setRecaptchaProviderType: (type: 'v2-checkbox' | 'v3') => void;
  setRecaptchaProviderSiteKey: (siteKey: string) => void;
  recaptchaProviderType: 'v2-checkbox' | 'v3';
  values: EvianSignUpData;
  setValues: (values: EvianSignUpData) => void;
}

export const EvianRegisterForm: React.FC<Props> = ({ zone,
  setIsSuccessScreenVisible,
  setRecaptchaProviderType,
  setRecaptchaProviderSiteKey,
  recaptchaProviderType,
  values,
  setValues
}) => {
  const { toast, loader } = useUIContext();
  const { executeV3 } = useGoogleReCaptcha();

  const [recaptchaTokenV2, setRecaptchaTokenV2] = useState<string>();

  const isValid = values.first_name.length > 0
    && values.last_name.length > 0
    && values.email.length > 0
    && values.phone_number.length > 0
    && values.zone.length > 0
    && values.agree;

  const resetForm = () => {
    setRecaptchaTokenV2(undefined);
    setRecaptchaProviderType('v3');
    setRecaptchaProviderSiteKey(process.env.REACT_APP_CAPTCHA_SITE_KEY_V3 as string);
    setValues({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      zone: zone || "",
      recaptcha_token: "",
      recaptcha_type: recaptchaProviderType.includes('v2') ? 'v2' : 'v3',
      agree: false,
    });
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    // @ts-ignore
    setValues((prev) => {
      const newValues = { ...prev, [e.target.name]: e.target.value };
      return newValues as EvianSignUpData;
    });
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!executeV3) {
      return;
    }
    loader.showLoader();
    try {
      if (recaptchaProviderType.includes('v3')) {
        const token = await executeV3('action');
        if (!token) {
          setRecaptchaProviderType('v2-checkbox');
          setRecaptchaProviderSiteKey(process.env.REACT_APP_CAPTCHA_SITE_KEY_V2 as string);
          return;
        }
        values.recaptcha_token = token;
        values.recaptcha_type = 'v3';
      } else if (recaptchaProviderType.includes('v2')) {
        if (!recaptchaTokenV2) {
          return;
        }
        values.recaptcha_token = recaptchaTokenV2;
        values.recaptcha_type = 'v2';
      }
      values.phone_number = formatUSPhoneNumber(values.phone_number);
      await API.Evian.signUpEvian(values);
      resetForm();
      setIsSuccessScreenVisible(true);
    } catch (error: any) {
      if (error.errorData.recaptcha_fallback) {
        setRecaptchaProviderType('v2-checkbox');
        setRecaptchaProviderSiteKey(process.env.REACT_APP_CAPTCHA_SITE_KEY_V2 as string);
      }
      toast.showToast(error.message, 'ERROR');
    } finally {
      loader.hideLoader();
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Field>
        <Label htmlFor="firts_name">First Name</Label>
        <Input
          id="first_name"
          name="first_name"
          placeholder="Enter your first name"
          value={values.first_name}
          autoComplete="name"
          required
          onChange={handleChange}
        />
      </Field>

      <Field>
        <Label htmlFor="last_name">Last Name</Label>
        <Input
          id="last_name"
          name="last_name"
          placeholder="Enter your last name"
          value={values.last_name}
          autoComplete="family-name"
          required
          onChange={handleChange}
        />
      </Field>

      <Field>
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="Enter your email address"
          value={values.email}
          autoComplete="email"
          inputMode="email"
          required
          onChange={handleChange}
        />
      </Field>

      <Field>
        <Label htmlFor="phone_number">Phone Number</Label>
        <Input
          id="phone_number"
          name="phone_number"
          placeholder="Enter your phone number"
          value={values.phone_number}
          autoComplete="tel"
          inputMode="tel"
          required
          onChange={handleChange}
        />
      </Field>

      <CheckboxRow>
        <input
          id="agree"
          name="agree"
          type="checkbox"
          checked={values.agree}
          // @ts-ignore
          onChange={(e) => setValues((prev) => {
            const newValues = { ...prev, agree: e.target.checked };
            return newValues as EvianSignUpData;
          })}
          required
        />
        <label htmlFor="agree">
          I have read and agree to the{" "}
          <a href="https://certified.promotrust.com/DanoneUS/evianF1Sweepstakes/rules/" target="_blank" rel="noopener noreferrer">Official evian F1 Recycling Sweepstakes Rules</a> and{" "}
          <a href="https://www.danonenorthamerica.com/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </label>
      </CheckboxRow>

      {
        recaptchaProviderType.includes('v2') && (
          <div>
            <GoogleReCaptchaCheckbox onChange={setRecaptchaTokenV2} />
          </div>
        )
      }

      <Submit type="submit" disabled={!isValid}>
        Submit Entry
      </Submit>
      <EvianRegisterFootnote />
    </Form>
  );
};